import {
  type ComponentProps,
  type FC,
  useCallback,
  useEffect,
  useState,
} from 'react';

import type { MetadataValues } from '@iconik-io/iconik-api/src/schema_patched/metadata';
import { OBJECT_TYPE_METADATA_VIEWS } from '@iconik-io/iconik-api/src/types/object_types';
import { elasticToForm } from '@iconik-io/shared-lib/src/utils/metadata_elastic_to_form';

import { errorMessage } from '../../ducks/messages/thunks';
import { getMetadataView } from '../../ducks/metadata_view/thunks';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import TextFieldWrapper from '../../ui/forms/field_wrappers/text/text';
import { EmptyComponent } from '../../utils/empty_component';
import EntityMetadata from '../entity_metadata/entity_metadata';
import permissionsManager, {
  type PermissionManagerInjectedProps,
} from '../permissions_manager/permissions_manager';
import type { SEGMENT_TYPE_GENERIC } from '../segment_form/constants';
import type { SegmentInputCommonProps } from './types';

const CATEGORY = 'generic';

const AssetMetadataPermissionsManager: FC<
  ComponentProps<typeof EntityMetadata> & PermissionManagerInjectedProps
> = (props) => {
  const updateProps = {
    ...props,
    disabled: props.disabled || !props.permissionsRolesMerge?.write,
  };
  return <EntityMetadata {...updateProps} />;
};

const AssetMetadataPermissionsManagerContainer = permissionsManager(
  AssetMetadataPermissionsManager,
);

const prefix = 'metadata.';

type Props = SegmentInputCommonProps & {
  segmentType: typeof SEGMENT_TYPE_GENERIC;
  value: MetadataValues;
  onChange: (value: MetadataValues) => void;
  metadataViewId?: string;
};

export const SegmentInputGeneric: FC<Props> = ({
  disabled,
  metadataViewId: initialMetadataViewId,
  onClick,
  onLayoutChanged,
  value,
}) => {
  const dispatch = useAppDispatch();

  const breakpoint = useAppSelector((state) => state.displayReducer.breakpoint);
  const metadataCategoryViews = useAppSelector(
    (state) => state.metadataCategories.metadataCategoryViews,
  );
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [metadataViewId, setMetadataViewId] = useState<string | undefined>(
    initialMetadataViewId,
  );

  const viewChangeHandler = useCallback(
    (viewId: string | undefined): void => {
      if (metadataViewId === viewId) {
        return;
      }
      setMetadataViewId(viewId);
      if (!viewId) {
        return;
      }
      setLoadingMetadata(true);
      dispatch(getMetadataView(viewId)).finally(() => {
        setLoadingMetadata(false);
        setTimeout(() => onLayoutChanged(), 0);
      });
    },
    [metadataViewId, onLayoutChanged],
  );

  const defaultCategory = metadataCategoryViews.segments?.[CATEGORY];

  useEffect(() => {
    if (!defaultCategory?.views) {
      return;
    }
    if (defaultCategory?.views && defaultCategory.views.length > 0) {
      const view = defaultCategory.views[0];
      if (view?.id && !metadataViewId) {
        viewChangeHandler(view.id);
      }
    } else {
      dispatch(
        errorMessage({
          text: 'There is no metadata views available',
        }),
      );
    }
  }, [defaultCategory?.views, viewChangeHandler, metadataViewId]);

  return (
    <>
      <AssetMetadataPermissionsManagerContainer
        aclsObjectId={metadataViewId}
        aclsType={OBJECT_TYPE_METADATA_VIEWS}
        breakpoint={breakpoint}
        buttonsComponent={EmptyComponent}
        categoryName="generic"
        componentClass="div"
        disabled={disabled}
        loading={loadingMetadata}
        metadataValues={elasticToForm(value, prefix)}
        objectType="segments"
        onFormClick={onClick}
        onLayoutChanged={onLayoutChanged}
        onViewChange={viewChangeHandler}
        viewId={metadataViewId}
        visibleMetadata
        prefix={prefix}
      />
      <TextFieldWrapper
        type="hidden"
        value={metadataViewId}
        name="metadata_view_id"
      />
    </>
  );
};
